import React from 'react';

export interface StatusPillProps {
  /**
   * The status value to display (e.g., 'active', 'inactive', 'completed')
   * Will be automatically converted to Title Case
   */
  status: string | undefined | null;

  /**
   * Size variant for the pill
   * @default 'sm'
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Additional className for custom styling
   */
  className?: string;
}

/**
 * Reusable status pill component with consistent styling
 * Handles text transformation and color mapping automatically
 */
const StatusPill: React.FC<StatusPillProps> = ({
  status,
  size = 'sm',
  className = ''
}) => {
  if (!status) return null;

  // Convert to Title Case
  const displayText = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();

  // Normalize status for color mapping
  const normalizedStatus = status.toLowerCase();

  // Size variants
  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1.5 text-sm',
    lg: 'px-4 py-2 text-base'
  };

  // Color mapping
  const getColorClasses = () => {
    switch (normalizedStatus) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <span
      className={`
        inline-flex items-center justify-center
        rounded-full font-medium
        ${sizeClasses[size]}
        ${getColorClasses()}
        ${className}
      `.trim().replace(/\s+/g, ' ')}
    >
      {displayText}
    </span>
  );
};

export default StatusPill;
